/* Tier 4 configurator — multi-line ATC.
 *
 * Reads selections from selects in [data-cf-picker], computes a live total,
 * and on ATC click POSTs to /cart/add.js with N items:
 *   - 1 base item (variant id = data-base-variant-id) carrying _config_id and
 *     all cosmetic selections as line item properties
 *   - 1 component item per chosen priced option (variant id = upgrade variant
 *     gid converted to numeric), carrying _config_id + _option_name + _choice
 *
 * Phase 1: cart shows multiple lines. cart-config-group.liquid groups them
 * visually by _config_id. Phase 2 (future) layers a Cart Transform Function
 * on top to merge into a single bundle line server-side.
 */
(function () {
  function gidToId(gid) {
    if (!gid) return null;
    const m = String(gid).match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  // Canonical Shopify money formatter (mirrors the Liquid `| money` filter,
  // incl. thousands separators) so JS-updated prices match the server-rendered ones.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || '£{{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    let value;
    switch ((fmt.match(placeholder) || [null, 'amount'])[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function genConfigId() {
    return 'cfg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function init(picker) {
    const baseVariantId = parseInt(picker.dataset.baseVariantId, 10);
    const basePence = parseFloat(picker.dataset.basePricePence) || 0;
    const productHandle = picker.dataset.productHandle;
    const cartRedirect = picker.dataset.cartRedirect || '/cart';
    // Size-selection persistence: family-scoped sessionStorage key. Empty when the
    // product is not part of a size group (no siblings to carry between).
    const carryKey = picker.dataset.sizeGroupKey ? 'cf-carry:' + picker.dataset.sizeGroupKey : '';

    const totalEl = picker.querySelector('[data-cf-total]');
    const atcBtn = picker.querySelector('[data-cf-atc]');
    const errorEl = picker.querySelector('[data-cf-error]');
    const baseComparePence = parseFloat(picker.dataset.baseComparePence) || 0;
    const campaignEnabled = picker.dataset.campaignEnabled === 'true';
    const baseCampaignPence = parseFloat(picker.dataset.baseCampaignPricePence) || basePence;
    const moneyFormat = picker.dataset.cfMoneyFormat || '£{{amount}}';
    const sectionId = picker.dataset.cfSection || '';
    const scheduleIdle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1200));
    // Qty selector (component-counter rendered next to the ATC). global.js drives +/-;
    // we read its value and multiply every bundle line + the displayed total by it.
    const atcLabel = atcBtn.querySelector('span') || atcBtn;
    const getQty = () => {
      const el = picker.querySelector('.js-counter-quantity');
      const n = parseInt(el && el.value, 10);
      return n > 0 ? n : 1;
    };

    // Paint the PER-UNIT configured price into the headline + sticky price blocks,
    // scoped by section id so related / recently-viewed prices are never touched.
    // The strikethrough "Was" scales by the same delta, so Now < Was always holds.
    function campaignUnitPence(unitPence) {
      if (!campaignEnabled) return unitPence;
      const deltaPence = unitPence - basePence;
      return Math.max(0, baseCampaignPence + deltaPence);
    }

    function paintHeadline(unitPence) {
      if (!sectionId) return;
      const deltaPence = unitPence - basePence;
      const displayPence = campaignUnitPence(unitPence);
      ['price-' + sectionId, 'sticky-price-' + sectionId].forEach(function (id) {
        const root = document.getElementById(id);
        if (!root) return;
        if (campaignEnabled) {
          root.querySelectorAll('.wt-product__price__campaign, .wt-product__price__final').forEach(function (n) {
            n.textContent = formatMoney(displayPence, moneyFormat);
          });
          root.querySelectorAll('.wt-product__price__current').forEach(function (n) {
            n.textContent = formatMoney(unitPence, moneyFormat);
          });
        } else {
          root.querySelectorAll('.wt-product__price__final').forEach(function (n) {
            n.textContent = formatMoney(unitPence, moneyFormat);
          });
        }
        if (baseComparePence > 0) {
          root.querySelectorAll('.wt-product__price__compare').forEach(function (n) {
            n.textContent = formatMoney(baseComparePence + deltaPence, moneyFormat);
          });
        }
      });
    }

    function recalc() {
      let unitPence = basePence;
      picker.querySelectorAll('[data-cf-group] select').forEach(sel => {
        const opt = sel.options[sel.selectedIndex];
        // opt.value is empty for choices with no upgrade variant, so gate on the
        // choice name (a real selection) rather than the value — otherwise deltas never add.
        if (opt && opt.dataset.cfName && opt.dataset.cfDelta) {
          unitPence += Math.round(parseFloat(opt.dataset.cfDelta) * 100);
        }
      });
      // Above-ATC total is qty-multiplied; the headline shows the per-unit price.
      totalEl.textContent = formatMoney(campaignUnitPence(unitPence) * getQty(), moneyFormat);
      paintHeadline(unitPence);
    }

    function syncSelectedLabel(group) {
      const sel = group.querySelector('select');
      const lbl = group.querySelector('[data-cf-selected-name]');
      const opt = sel.options[sel.selectedIndex];
      lbl.textContent = realOption(sel) ? opt.dataset.cfName : '';
    }

    function syncPreview(group) {
      const sel = group.querySelector('select');
      const preview = group.querySelector('[data-cf-preview]');
      const img = group.querySelector('[data-cf-preview-img]');
      const opt = sel.options[sel.selectedIndex];
      const src = opt && opt.dataset.cfImage;
      if (src) {
        img.src = src;
        preview.hidden = false;
      } else {
        preview.hidden = true;
        img.removeAttribute('src');
      }
    }

    // ---- Size-selection persistence (carry config across sibling-size navigation) ----
    function readCarry() {
      if (!carryKey) return {};
      try { return JSON.parse(sessionStorage.getItem(carryKey)) || {}; } catch (e) { return {}; }
    }
    function writeCarry(map) {
      if (!carryKey) return;
      try { sessionStorage.setItem(carryKey, JSON.stringify(map)); } catch (e) {}
    }
    // A "real" selection = a chosen option with a name, not the disabled placeholder.
    function realOption(sel) {
      const opt = sel && sel.options[sel.selectedIndex];
      return (opt && opt.dataset.cfName && !opt.hasAttribute('data-cf-default')) ? opt : null;
    }
    function captureSelections() {
      if (!carryKey) return;
      const map = readCarry();
      picker.querySelectorAll('[data-cf-group]').forEach(group => {
        const name = group.dataset.optionName;
        if (!name) return;
        const opt = realOption(group.querySelector('select'));
        if (opt) map[name] = { ovid: opt.dataset.cfOvid || '', name: opt.dataset.cfName };
      });
      writeCarry(map);
    }
    // Swatch visual state derives from the <select> (the source of truth), matched by
    // OpenCart option-value id (data-cf-ovid) when present, falling back to choice name.
    function syncSwatchSelected(group) {
      const opt = realOption(group.querySelector('select'));
      const activeOvid = opt ? (opt.dataset.cfOvid || '') : '';
      const activeName = opt ? opt.dataset.cfName : null;
      group.querySelectorAll('[data-cf-swatch]').forEach(sw => {
        let on;
        if (activeOvid) on = (sw.getAttribute('data-cf-ovid') || '') === activeOvid;
        else on = activeName !== null && (sw.getAttribute('data-cf-name') || '') === activeName;
        sw.classList.toggle('cf-acc__swatch--selected', on);
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }
    // Paint the in-accordion banner (large image + name pill) from the selected option,
    // so a restored choice shows correctly on first expand. The banner <img> is
    // loading="lazy" inside a hidden body, so setting src here does not fetch until the
    // group is expanded (keeps restore off the load-time critical path).
    function syncBanner(group) {
      const opt = realOption(group.querySelector('select'));
      const img = group.querySelector('[data-cf-banner-img]');
      const pill = group.querySelector('[data-cf-banner-name]');
      const src = opt ? (opt.dataset.cfImage || '') : '';
      if (img) {
        if (src) { img.src = src; img.alt = opt.dataset.cfName || ''; }
        else { img.removeAttribute('src'); img.alt = ''; }
      }
      if (pill) pill.textContent = opt ? opt.dataset.cfName : '';
    }
    // Enable "Select & Continue" only once the group has a real (non-placeholder) choice.
    function syncContinue(group) {
      const btn = group.querySelector('[data-cf-continue]');
      if (btn) btn.disabled = !realOption(group.querySelector('select'));
    }
    function compactShopifyImageUrl(src, width) {
      if (!src || !/\/cdn\/shop\/files\/|cdn\.shopify\.com\/s\/files\//.test(src)) return src;
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.href;
      } catch (e) {
        return src;
      }
    }
    // Paint the collapsed-tab thumbnail: selected swatch image, else a ✓ for
    // text/no-image choices. Toggles cf-acc__head--selected (CSS hides the +).
    function syncHeadThumb(group) {
      const head = group.querySelector('[data-cf-acc-head]');
      const thumb = group.querySelector('[data-cf-thumb]');
      const opt = realOption(group.querySelector('select'));
      if (head) head.classList.toggle('cf-acc__head--selected', !!opt);
      if (!thumb) return;
      thumb.textContent = '';
      if (!opt) return;
      const sw = group.querySelector('.cf-acc__swatch--selected');
      const img = sw && sw.querySelector('img');
      if (img) {
        const i = document.createElement('img');
        i.src = compactShopifyImageUrl(img.currentSrc || img.src, 96);
        i.alt = '';
        i.width = 48;
        i.height = 48;
        i.loading = 'lazy';
        i.decoding = 'async';
        thumb.appendChild(i);
      } else {
        const c = document.createElement('span');
        c.className = 'cf-acc__thumb-check'; c.textContent = '✓';
        thumb.appendChild(c);
      }
    }
    function clearCarry() {
      if (!carryKey) return;
      try { sessionStorage.removeItem(carryKey); } catch (e) {}
    }
    // Re-apply stored choices on load. Runs after defaults so carried choices win. Sets
    // the matching <select> option + dispatches change (NOT swatch.click()), so the
    // existing change handler does all the syncing. Missing group/choice is skipped.
    function restoreSelections() {
      if (!carryKey) return;
      const map = readCarry();
      const names = Object.keys(map);
      if (!names.length) return;
      const groups = picker.querySelectorAll('[data-cf-group]');
      requestAnimationFrame(() => {
        names.forEach(optionName => {
          const entry = map[optionName] || {};
          let group = null;
          groups.forEach(g => { if (g.dataset.optionName === optionName) group = g; });
          if (!group) return;
          const sel = group.querySelector('select');
          if (!sel) return;
          // Match the stored choice by LABEL (data-cf-name). Choice ids (ovid) are NOT
          // stable across size ranges (each range uses different configurator_option
          // metaobjects), but the human labels are consistent, so the name is the
          // reliable cross-size key. Names are unique within a group.
          let match = null;
          for (let i = 0; i < sel.options.length; i++) {
            if (entry.name && sel.options[i].dataset.cfName === entry.name) { match = sel.options[i]; break; }
          }
          if (!match) return;
          match.selected = true;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    picker.addEventListener('change', (e) => {
      // Qty changes (the shared component-counter) just refresh the displayed total.
      if (e.target.classList.contains('js-counter-quantity')) { recalc(); return; }
      const group = e.target.closest('[data-cf-group]');
      if (!group) return;
      group.classList.remove('cf-picker__group--invalid');
      syncSelectedLabel(group);
      syncPreview(group);
      syncSwatchSelected(group);
      syncBanner(group);
      syncHeadThumb(group);
      syncContinue(group);
      recalc();
      captureSelections();
    });

    atcBtn.addEventListener('click', async () => {
      errorEl.hidden = true;
      errorEl.textContent = '';

      // Validate. Distinguish the disabled placeholder (`data-cf-default`)
      // from £0 values like "No Headboard" — those have a name but empty
      // variantGid because no upgrade component is needed.
      let valid = true;
      const priced = [];
      picker.querySelectorAll('[data-cf-group]').forEach(group => {
        const sel = group.querySelector('select');
        const required = group.dataset.required === 'true';
        const opt = sel.options[sel.selectedIndex];
        const isDefault = !opt || opt.hasAttribute('data-cf-default') || !opt.dataset.cfName;
        if (required && isDefault) {
          valid = false;
          group.classList.add('cf-picker__group--invalid');
          return;
        }
        if (!isDefault) {
          const delta = parseFloat(opt.dataset.cfDelta || 0);
          const variantGid = opt.dataset.cfVariantId || sel.value;
          if (delta <= 0 && group.dataset.cfGroup !== 'priced') return;
          priced.push({
            variantGid: variantGid,  // may be empty for £0 options — handled downstream
            optionName: group.dataset.optionName,
            choiceName: opt.dataset.cfName,
            ovid: opt.dataset.cfOvid,
            delta: delta,
          });
        }
      });

      if (!valid) {
        errorEl.textContent = 'Please complete all required selections.';
        errorEl.hidden = false;
        return;
      }

      const cosmeticProps = {};
      picker.querySelectorAll('[data-cf-group="cosmetic"]').forEach(group => {
        const sel = group.querySelector('select');
        const opt = sel.options[sel.selectedIndex];
        const hasComponentVariant = opt && gidToId(opt.dataset.cfVariantId);
        if (sel.value && !hasComponentVariant) {
          cosmeticProps[group.dataset.optionName] = sel.value;
        }
      });

      const configId = genConfigId();
      const qty = getQty();
      const items = [];

      // £0 priced picks ("No Headboard", "Verona Silver", etc.) have no upgrade
      // variant — they don't get their own line item, so attach them to the
      // base item as visible properties (no underscore prefix → shown in cart).
      const zeroPricedProps = {};
      priced.forEach(p => {
        if (!gidToId(p.variantGid)) {
          zeroPricedProps[p.optionName] = p.choiceName;
        }
      });

      // Base line — carries cosmetic + £0 priced choices and the config marker
      const baseProps = Object.assign(
        { _config_id: configId, _is_base: 'true', _product_handle: productHandle },
        cosmeticProps,
        zeroPricedProps
      );
      items.push({ id: baseVariantId, quantity: qty, properties: baseProps });

      // Component lines — one per priced selection with an upgrade variant
      priced.forEach(p => {
        const vid = gidToId(p.variantGid);
        if (!vid) return;
        items.push({
          id: vid,
          quantity: qty,
          properties: {
            _config_id: configId,
            _is_component: 'true',
            _option_name: p.optionName,
            _choice: p.choiceName,
            _oc_ovid: p.ovid || '',
          },
        });
      });

      atcBtn.disabled = true;
      atcLabel.textContent = 'Adding…';

      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.description || body.message || `Add to cart failed (${r.status})`);
        }
        clearCarry();
        // Refresh Wonder's cart drawer + open it. The drawer listens for
        // `cart-drawer:refresh`; opening is via toggleDrawerClasses() (no
        // `open()` method exists). Fall back to /cart only if there is no
        // drawer element on the page.
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh', {
          detail: { configId },
          bubbles: true,
          composed: true,
        }));
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          if (!drawer.hasAttribute('open') && typeof drawer.toggleDrawerClasses === 'function') {
            drawer.toggleDrawerClasses();
          }
        } else {
          window.location.href = cartRedirect;
        }
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } finally {
        atcBtn.disabled = false;
        atcLabel.textContent = 'Add to cart';
      }
    });

    // Auto-default colour/material tabs to their first FREE choice when nothing is
    // selected, so the tab opens with a real banner preview instead of the blank
    // shimmer placeholder. Delta-0 gate => price never changes; structural
    // (configuration) and image-less (delivery) tabs are left alone. Carried/restored
    // choices still win (restoreSelections runs after, below).
    picker.querySelectorAll('[data-cf-group]').forEach(g => {
      if (g.classList.contains('cf-acc--structural')) return;
      const sel = g.querySelector('select');
      if (!sel || realOption(sel)) return;
      if (!g.querySelector('[data-cf-swatch] img')) return;
      const first = sel.querySelector('option:not([data-cf-default])');
      if (!first || parseFloat(first.dataset.cfDelta || '0')) return;
      first.selected = true;
    });

    // Init labels + banner once on load
    picker.querySelectorAll('[data-cf-group]').forEach(g => {
      syncSelectedLabel(g);
      syncPreview(g);
      syncSwatchSelected(g);
      syncBanner(g);
      syncHeadThumb(g);
      syncContinue(g);
    });
    recalc();

    // Paid colour tabs (every choice adds cost) can't be auto-selected without
    // changing the price. Their blank-state preview image is decorative, so defer
    // it until the browser is idle to keep those downloads out of the LCP window.
    scheduleIdle(() => {
      if (!document.documentElement.contains(picker)) return;
      picker.querySelectorAll('[data-cf-group]').forEach(g => {
        if (g.classList.contains('cf-acc--structural')) return;
        const sel = g.querySelector('select');
        if (!sel || realOption(sel)) return;
        if (!g.querySelector('[data-cf-swatch] img')) return;
        const first = sel.querySelector('option:not([data-cf-default])');
        const img = g.querySelector('[data-cf-banner-img]');
        const src = first ? (first.dataset.cfImage || '') : '';
        if (img && src && !img.getAttribute('src')) { img.src = src; img.alt = ''; }
      });
    }, { timeout: 3000 });

    restoreSelections();
  }

  function boot() {
    document.querySelectorAll('[data-cf-picker]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
